import { Box } from '@deckee/deck-hand';
import React, { FC, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { telemetry } from '../../../services/backend';
import { captureEvent } from '../../../services/event-capture';
import { get, set } from '../../../services/local-storage';
import DesktopSidebar from './components/DesktopSidebar';
import {
  SIDEBAR_TRANSITION,
  SIDEBAR_WIDTH_COLLAPSED,
  SIDEBAR_WIDTH_EXPANDED,
} from './sidebarConstants';
import useDesktopSidebar from './useDesktopSidebar';

export const STORAGE_NAMESPACE = 'desktopSidebar';
export const STORAGE_KEY = 'collapsed';

/**
 * Holds the map, page holder and app controls. On desktop it is pushed right
 * by the sidebar width so everything that was positioned against the left
 * edge of the screen now sits against the sidebar instead.
 */
const MainArea = styled(Box)<{ $offset: number }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: ${({ $offset }) => $offset}px;
  right: 0;
  overflow: hidden;
  transition: left ${SIDEBAR_TRANSITION};
`;

interface DesktopNavLayoutProps {
  children: React.ReactNode;
}

const DesktopNavLayout: FC<DesktopNavLayoutProps> = ({ children }) => {
  const { visible: sidebarVisible, enabled: sidebarEnabled } =
    useDesktopSidebar();
  const [collapsed, setCollapsed] = useState(false);
  // Wait for the saved preference before first showing the sidebar, so a
  // collapsed sidebar doesn't open and then animate shut on every reload.
  const [preferenceLoaded, setPreferenceLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    get(STORAGE_NAMESPACE, STORAGE_KEY)
      .then((stored: unknown) => {
        if (!cancelled && stored === true) setCollapsed(true);
      })
      .catch((err: unknown) => {
        telemetry(
          telemetry.ERROR,
          `[DesktopNavLayout] Error reading collapsed state ${JSON.stringify(err)}`,
        );
      })
      .finally(() => {
        if (!cancelled) setPreferenceLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleToggleCollapsed = useCallback(() => {
    const next = !collapsed;
    captureEvent({
      type: 'desktop-sidebar: toggle collapsed',
      payload: { collapsed: next },
      sendToAmplitude: true,
    });
    setCollapsed(next);
    set(STORAGE_NAMESPACE, STORAGE_KEY, next, true).catch((err: unknown) => {
      telemetry(
        telemetry.ERROR,
        `[DesktopNavLayout] Error saving collapsed state ${JSON.stringify(err)}`,
      );
    });
  }, [collapsed]);

  // Mapbox only tracks window resizes, so nudge it once the main area has
  // finished resizing after a collapse/expand.
  const handleTransitionEnd = useCallback((event: React.TransitionEvent) => {
    if (event.target === event.currentTarget && event.propertyName === 'left') {
      window.dispatchEvent(new Event('resize'));
    }
  }, []);

  // Only mount the sidebar where it is shown, so it doesn't fetch alert
  // counts on mobile.
  const showSidebar = sidebarVisible && preferenceLoaded;
  let offset = 0;
  if (showSidebar) {
    offset = collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED;
  }

  return (
    <>
      {showSidebar && (
        <DesktopSidebar
          collapsed={collapsed}
          disabled={!sidebarEnabled}
          onToggleCollapsed={handleToggleCollapsed}
        />
      )}
      <MainArea $offset={offset} onTransitionEnd={handleTransitionEnd}>
        {children}
      </MainArea>
    </>
  );
};

export default DesktopNavLayout;
